import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAccountSettingsStore } from '../../../../Zustand/accountSettingsStore';
import {
  ISciChartSurfaceBase,
  NumberRange,
  SciChartOverview,
  SciChartSurface,
  SciChartVerticalGroup,
} from 'scichart';
import { SciChartReact, SciChartGroup, IInitResult } from 'scichart-react';
import { Loader } from '@progress/kendo-react-indicators';
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from '../../../../messages/en-US';
import { synchronizeXVisibleRanges } from '../../../Shared/SciChart/ChartConfiguration';
import { overviewOptions } from '../../../Shared/SciChart/Overview';
import { getWorkOrdersAPI } from './WorkOrdersApi';
import { ApiScheduleGroupRow, WorkOrdersInput, WorkOrdersComplianceSettings, ScheduleClickPayload, UnscheduleClickPayload, toWorkOrdersGroupRows } from './WorkOrdersTypes';
import { WorkOrdersDetailPanelItem } from './WorkOrdersDetailPanel';
import { WorkOrdersUnschedulePanelItem } from './WorkOrdersUnschedulePanel';
import { arrayExtent } from '../../../Shared/Utilities/ArrayUtilities';

interface Props {
  data: WorkOrdersInput;
  /** Fallback X-axis when the result has no compliance windows (same as last filter used for the request). */
  reportRangeStart?: Date | null;
  reportRangeEnd?: Date | null;
  /** Shows Kendo Loader overlay on the chart while true */
  loading?: boolean;
  onScheduleClick?: (payload: ScheduleClickPayload) => void;
  compliance?: WorkOrdersComplianceSettings;
  /** Row grouping mode — Schedule merges assets, so Y labels show only the schedule. */
  groupBy?: 'AssetAndSchedule' | 'Schedule';
}

/** Pixels per category row (maps 1 Y unit → ROW_HEIGHT_PX); lower = tighter rows */
const ROW_HEIGHT_PX = 60;
/** Height of the separate header surface (date axis only; matches axisThickness + minimal viewport) */
const HEADER_DATE_AXIS_PX = 42;
/** Bottom X-axis gutter on the body surface (grid strip; see axisThickness on body X axis) */
const BODY_BOTTOM_AXIS_RESERVE_PX = 16;
/** Host height for SciChartOverview (linked to body surface — same pattern as SciChartNestedOverview elsewhere). */
const OVERVIEW_HEIGHT_PX = 50;
/** Filters, title row, layout chrome — tune if vertical scrollbar still clips the card */
const PAGE_CHROME_ABOVE_CHART_PX = 225;
/** Whole chart card (scrollable chart block + pinned overview) */
const WORK_ORDERS_CARD_MAX_HEIGHT = `calc(100vh - ${PAGE_CHROME_ABOVE_CHART_PX}px)`;

export default function WorkOrders({
  data,
  reportRangeStart = null,
  reportRangeEnd = null,
  loading = false,
  onScheduleClick,
  compliance,
  groupBy,
}: Props) {
  const timezone = useAccountSettingsStore((state) => state.accountTimezone);
  const localizationService = useLocalization();
  const loadingAriaLabel = localizationService.toLanguageString(
    'custom.loading',
    enMessages.custom.loading
  );

  const onScheduleClickRef = useRef(onScheduleClick);
  onScheduleClickRef.current = onScheduleClick;

  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleClickPayload | null>(null);
  const [selectedUnschedule, setSelectedUnschedule] = useState<UnscheduleClickPayload | null>(null);

  /** SciChartOverview instance — must be deleted when chart remounts */
  const sciChartOverviewRef = useRef<SciChartOverview | null>(null);
  /** DOM host for SciChartOverview.create(bodySurface, mount, options) */
  const overviewMountRef = useRef<HTMLDivElement | null>(null);
  /** Align overview strip to body plot area (overview axes are invisible so layout collapses without this). */
  const overviewLayoutUnsubRef = useRef<(() => void) | null>(null);

  const groupData = useMemo((): ApiScheduleGroupRow[] => toWorkOrdersGroupRows(data), [data]);

  const handleScheduleClick = useCallback((p: ScheduleClickPayload) => {
    setSelectedUnschedule(null);
    setSelectedSchedule(p);
    onScheduleClickRef.current?.(p);
  }, []);

  const handleUnscheduleGroupClick = useCallback((p: UnscheduleClickPayload) => {
    setSelectedSchedule(null);
    setSelectedUnschedule(p);
  }, []);

  useEffect(() => {
    setSelectedSchedule(null);
    setSelectedUnschedule(null);
  }, [data]);

  useEffect(() => {
    return () => {
      overviewLayoutUnsubRef.current?.();
      overviewLayoutUnsubRef.current = null;
      sciChartOverviewRef.current?.delete();
      sciChartOverviewRef.current = null;
    };
  }, []);

  /** Pixels matching body SciChartSurface.seriesViewRect so overview matches the plot (not full surface width). */
  const [overviewPlotMetrics, setOverviewPlotMetrics] = useState<{
    offsetLeft: number;
    plotWidth: number;
  } | null>(null);

  const { sharedXRange, xRangeLimit } = useMemo(() => {
    /** Full extent of returned windows + unschedule events. */
    const times: number[] = [];
    for (const g of groupData) {
      for (const w of g.workOrders) {
        times.push(new Date(w.startDateUtc).getTime() / 1000, new Date(w.endDateUtc).getTime() / 1000);
      }
      for (const u of g.unscheduleEvents ?? []) {
        times.push(new Date(u.occurred).getTime() / 1000);
      }
    }
    const timesExtent = arrayExtent(times);
    const dataMin = times.length > 0 ? timesExtent.min : undefined;
    const dataMax = times.length > 0 ? timesExtent.max : undefined;

    /** Initial visible window: the selected date filter is authoritative (fallback to data extent). */
    let visMin: number;
    let visMax: number;
    if (reportRangeStart && reportRangeEnd) {
      visMin = reportRangeStart.getTime() / 1000;
      visMax = reportRangeEnd.getTime() / 1000;
    } else if (dataMin !== undefined && dataMax !== undefined) {
      const SEVEN_DAYS = 7 * 24 * 3600;
      visMin = dataMin - SEVEN_DAYS;
      visMax = dataMax;
    } else {
      return { sharedXRange: new NumberRange(0, 1), xRangeLimit: new NumberRange(0, 1) };
    }

    /**
     * Pan/zoom limit = union of the selected window and all returned data, so the user can
     * scroll to schedules that fall partly outside the selected range (what the overview shows).
     */
    const limMin = dataMin !== undefined ? Math.min(visMin, dataMin) : visMin;
    const limMax = dataMax !== undefined ? Math.max(visMax, dataMax) : visMax;

    return {
      sharedXRange: new NumberRange(visMin, visMax),
      xRangeLimit: new NumberRange(limMin, limMax),
    };
  }, [groupData, reportRangeStart, reportRangeEnd]);

  const workOrdersApi = useMemo(
    () =>
      getWorkOrdersAPI({
        apiData: groupData,
        groupBy,
        sharedXRange,
        xRangeLimit,
        timezone,
        onScheduleClick: handleScheduleClick,
        onUnscheduleGroupClick: handleUnscheduleGroupClick,
      }),
    [groupData, groupBy, sharedXRange, xRangeLimit, timezone, handleScheduleClick, handleUnscheduleGroupClick]
  );

  const chartInstanceKey = useMemo(
    () => `workOrders-${groupData.length}-${sharedXRange.min}-${sharedXRange.max}`,
    [groupData.length, sharedXRange.min, sharedXRange.max]
  );

  useEffect(() => {
    setOverviewPlotMetrics(null);
  }, [chartInstanceKey]);

  const bodyChartHeightPx = groupData.length * ROW_HEIGHT_PX + BODY_BOTTOM_AXIS_RESERVE_PX;

  // Fit the chart card between its actual top (which moves as the legend wraps on
  // small screens) and the bottom of the viewport, so the overview is always visible.
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardMaxHeightPx, setCardMaxHeightPx] = useState<number | null>(null);
  useEffect(() => {
    function recompute() {
      const top = cardRef.current?.getBoundingClientRect().top ?? 0;
      // Covers the page wrapper's bottom margin (m-5 = 20px) so the card + its margin
      // box stays within the viewport — otherwise the page shows a thin scrollbar.
      const BOTTOM_GUTTER_PX = 24;
      setCardMaxHeightPx(Math.max(260, window.innerHeight - top - BOTTOM_GUTTER_PX));
    }
    recompute();
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [groupData.length]);

  const cardMaxHeight = cardMaxHeightPx != null ? `${cardMaxHeightPx}px` : WORK_ORDERS_CARD_MAX_HEIGHT;

  const onChartsInit = useCallback((initResults: IInitResult<ISciChartSurfaceBase>[]) => {
    const hs = initResults[0]?.sciChartSurface as SciChartSurface | undefined;
    const bs = initResults[1]?.sciChartSurface as SciChartSurface | undefined;
    if (!hs || !bs) return;

    synchronizeXVisibleRanges([hs, bs], () => true);

    const verticalGroup = new SciChartVerticalGroup();
    verticalGroup.addSurfaceToGroup(hs);
    verticalGroup.addSurfaceToGroup(bs);

    overviewLayoutUnsubRef.current?.();
    overviewLayoutUnsubRef.current = null;

    const syncOverviewPlotMetrics = () => {
      if (bs.isDeleted) return;
      const r = bs.seriesViewRect;
      /** seriesViewRect is in canvas/device pixels; divide by DPR to get CSS pixels for DOM positioning. */
      const dpr = window.devicePixelRatio || 1;
      setOverviewPlotMetrics({ offsetLeft: r.x / dpr, plotWidth: r.width / dpr });
    };
    syncOverviewPlotMetrics();
    const onBodyRendered = () => syncOverviewPlotMetrics();
    bs.rendered.subscribe(onBodyRendered);
    overviewLayoutUnsubRef.current = () => bs.rendered.unsubscribe(onBodyRendered);

    sciChartOverviewRef.current?.delete();
    sciChartOverviewRef.current = null;

    void (async () => {
      await new Promise<void>((resolve) => queueMicrotask(() => resolve()));
      const mountEl = overviewMountRef.current;
      if (!mountEl) return;
      try {
        /** Omit transformRenderableSeries — it forces FastLine clones and hides colored band segments in the overview. */
        const { transformRenderableSeries: _omitTransform, ...overviewForWorkOrders } = overviewOptions;
        const overview = await SciChartOverview.create(bs, mountEl, {
          ...overviewForWorkOrders,
          mainAxisId: 'xDate',
          secondaryAxisId: 'yAssets',
        });
        sciChartOverviewRef.current = overview;
      } catch (e) {
        console.error('SciChartOverview.create failed', e);
      }
    })();
  }, []);

  const handleClose = useCallback(() => setSelectedSchedule(null), []);
  const handleCloseUnschedule = useCallback(() => setSelectedUnschedule(null), []);

  return (
    <>
        <div className="pt-0">
          <div className="bg-white">
            {/*
              One scroll region for date header + row grid only (same width). Overview is outside that
              region so the brush timeline stays visible (max height reserves OVERVIEW_HEIGHT_PX).
            */}
            <div
              ref={cardRef}
              className="relative flex max-w-full flex-col overflow-hidden rounded border border-gray-300 bg-white"
              style={{ maxHeight: cardMaxHeight }}
              aria-busy={loading}
            >
              {loading ? (
                <div
                  className="pointer-events-auto absolute inset-0 z-30 flex items-center justify-center bg-white/80"
                  aria-live="polite"
                  aria-label={loadingAriaLabel}
                >
                  <Loader size="medium" type="pulsing" />
                </div>
              ) : null}
              <div
                className="wo-chart-scroll min-h-0 flex-1 overflow-y-auto bg-white"
                style={{ scrollbarGutter: 'stable' }}
              >
                <SciChartGroup key={chartInstanceKey} onInit={onChartsInit}>
                  <SciChartReact
                    key={`${chartInstanceKey}-header`}
                    initChart={workOrdersApi.initHeaderChart}
                    fallback={<div/>}
                    style={{
                      width: '100%',
                      height: HEADER_DATE_AXIS_PX,
                      position: 'sticky',
                      top: 0,
                      zIndex: 10,
                      backgroundColor: '#ffffff',
                    }}
                  />
                  <SciChartReact
                    key={`${chartInstanceKey}-body`}
                    initChart={workOrdersApi.initBodyChart}
                    fallback={<div/>}
                    style={{ width: '100%', height: bodyChartHeightPx }}
                  />
                </SciChartGroup>
              </div>
              <div className="shrink-0 border-t border-gray-200 bg-[#fafafa]">
                <div
                  ref={overviewMountRef}
                  className="box-border"
                  style={{
                    marginLeft: overviewPlotMetrics?.offsetLeft ?? 0,
                    width: overviewPlotMetrics?.plotWidth ?? '100%',
                    height: OVERVIEW_HEIGHT_PX,
                    minHeight: OVERVIEW_HEIGHT_PX,
                  }}
                  aria-hidden
                />
              </div>
            </div>
          </div>
        </div>

        {selectedSchedule && (
          <>
            <div
              className="fixed inset-0 z-40"
              style={{ background: 'rgba(0,0,0,0.15)' }}
              onClick={handleClose}
            />
            <div
              className="fixed top-0 right-0 z-50 h-full shadow-xl"
              style={{ width: 'min(100vw, 750px)' }}
            >
              <WorkOrdersDetailPanelItem
                {...selectedSchedule}
                onClose={handleClose}
                compliance={compliance}
                timezone={timezone}
              />
            </div>
          </>
        )}

        {selectedUnschedule && (
          <>
            <div
              className="fixed inset-0 z-40"
              style={{ background: 'rgba(0,0,0,0.15)' }}
              onClick={handleCloseUnschedule}
            />
            <div
              className="fixed top-0 right-0 z-50 h-full shadow-xl"
              style={{ width: 'min(100vw, 550px)' }}
            >
              <WorkOrdersUnschedulePanelItem
                {...selectedUnschedule}
                onClose={handleCloseUnschedule}
              />
            </div>
          </>
        )}
    </>
  );
}
